import { hmac } from "@noble/hashes/hmac.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, hexToBytes } from "@noble/hashes/utils.js";

// CAR-63 — single source of truth for the rolling BankID QR data string.
// Previously duplicated byte-for-byte across `apps/mobile/src/auth/`
// and `apps/admin/src/auth/`, with explicit "keep in sync" comments at
// the top of each copy. Drift here means one surface produces auth
// codes the BankID backend rejects.
//
// Per the BankID Relying Party Guidelines, the rendered string is:
//
//   bankid.<qrStartToken>.<T>.<qrAuthCode>
//
// where T is integer seconds since init and qrAuthCode is
// HMAC-SHA256(qrStartSecret, T) lower-case hex.
//
// qrStartSecret is hex-encoded in production; mock-mode values are plain
// strings (`mock-qrStartSecret-…`), so we fall back to UTF-8 bytes when
// hex decoding fails. The auth code is deterministic either way; the
// API's mock collector ignores it.

export function computeBankIdQrData(
  qrStartToken: string,
  qrStartSecret: string,
  initTimeMs: number,
  nowMs: number,
): string {
  const T = Math.max(0, Math.floor((nowMs - initTimeMs) / 1000));
  const keyBytes = hexBytesOrUtf8(qrStartSecret);
  const message = new TextEncoder().encode(T.toString());
  const authCode = bytesToHex(hmac(sha256, keyBytes, message));
  return `bankid.${qrStartToken}.${T}.${authCode}`;
}

function hexBytesOrUtf8(secret: string): Uint8Array {
  if (/^[0-9a-fA-F]+$/.test(secret) && secret.length % 2 === 0) {
    try {
      return hexToBytes(secret);
    } catch {
      // Fall through to UTF-8.
    }
  }
  return new TextEncoder().encode(secret);
}
